/**
 * EmptyState — the canonical "nothing here" presentation across every
 * Ordo app. A bordered rounded box containing a page-relevant icon, bold
 * heading, subtle one-line subtitle, and (encouraged) a single action
 * button. Centered in the available space.
 *
 * ## Rules of the road
 *
 * 1. **Heading is a single noun-phrase**, no trailing period:
 *    `No Skills`, `No Tools`, `No Conversations`. Concept-noun caps
 *    follow the rest of Ordo (Skills, Tools, etc.).
 *
 * 2. **Description is one short line ending in a period**, written as
 *    a state-of-the-world sentence — what the user will see here once
 *    they have content. Examples:
 *      • "Your Skills will appear here."
 *      • "Tools the agent can call will appear here."
 *      • "Ask a question to begin." (only when an action is implied)
 *    Keep it under ~80 characters.
 *
 * 3. **Icon is page-relevant** — match what the page is about (Scroll
 *    for Skills, Wrench for Tools, MessageSquare for Chats, etc.).
 *    Pass it as a lucide-react `<Icon />` element; the primitive will
 *    size and tint it.
 *
 * 4. **Two states for filtered surfaces**: render a different copy when
 *    the page has data but filters hid it all. Example:
 *      • Has-data + filtered: "No Skills match these filters" + "Try clearing a filter."
 *      • Truly empty:         "No Skills"                      + "Your Skills will appear here."
 *    Different copy because the action is different (clear filter vs.
 *    create new). The caller handles the branching.
 *
 * 5. **A single action is encouraged.** Pass `action={<Button …>}` — a
 *    primary pill button (optionally with a leading icon, e.g.
 *    `<Plus /> Create email` or `<Mail /> Email Us`). Keep it to ONE
 *    button; don't stack CTAs.
 *
 * 6. **One per page section.** Lists, panels, and tabs each get a
 *    single EmptyState directly inside their content area. Don't
 *    nest them.
 *
 * ## Variant
 *
 * - `bordered` (DEFAULT, fleet-wide) — the rounded bordered box. Use
 *   everywhere.
 * - `plain` — borderless; opt out only for embedded/inline cases where a
 *   box would be wrong (e.g. already inside a card or a tight cell).
 *
 * ## Anti-patterns
 *
 * - Don't add illustrations / hero art / circles around the icon.
 * - Don't add multiple paragraphs of "here's how this feature works."
 * - Don't reuse a generic icon (Sparkles, Inbox) when a more specific
 *   page-icon exists. The icon is a literal index of the page noun.
 */

import type { ReactNode } from 'react'

import { cn } from '../cn'

export interface EmptyStateProps {
  /** Optional page-relevant icon (lucide-react element). Sized and tinted by the primitive. */
  icon?: ReactNode
  /** Bold heading. Single noun-phrase, no trailing period. */
  title: string
  /** Optional subtle one-line subtitle. State-of-the-world sentence ending in a period. */
  description?: ReactNode
  /** Encouraged action slot — a single primary button (optionally with a leading icon). */
  action?: ReactNode
  /**
   * Visual variant. Defaults to `bordered` (the rounded box) fleet-wide.
   * Use `plain` only to opt out where a box would be wrong.
   */
  variant?: 'bordered' | 'plain'
  /** Override the vertical padding (default `py-24`). */
  padding?: 'sm' | 'md' | 'lg'
  className?: string
}

const PADDING_CLASS: Record<NonNullable<EmptyStateProps['padding']>, string> = {
  sm: 'py-12',
  md: 'py-16',
  lg: 'py-24',
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  variant = 'bordered',
  padding = 'lg',
  className,
}: EmptyStateProps) {
  return (
    <div className={cn('grid place-items-center', PADDING_CLASS[padding], className)}>
      <div
        className={cn(
          'flex flex-col items-center text-center',
          variant === 'bordered' && 'w-full max-w-md rounded-xl border border-border bg-card px-8 py-12',
        )}>
        {icon ? (
          <span className="mb-3 grid h-10 w-10 place-items-center text-muted-foreground/60 [&>svg]:h-10 [&>svg]:w-10">
            {icon}
          </span>
        ) : null}
        <h3 className="text-base font-semibold">{title}</h3>
        {description ? <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p> : null}
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  )
}
